import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { LOGO_URL } from "@/lib/sps-content";
import { Menu, X } from "lucide-react";

const LINKS = [
  { id: "nosotros", label: "Nosotros", route: "/nosotros" },
  { id: "servicios", label: "Servicios", route: "/servicios" },
  { id: "catalogo", label: "Catálogo", href: "https://integracion.beflodesing.com/" },
  { id: "contacto", label: "Contacto", route: "/contacto" },
];

export const Navbar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState("");

  useEffect(() => {
    if (location.pathname === "/contacto") {
      setActive("contacto");
      return;
    }
    if (location.pathname === "/nosotros") {
      setActive("nosotros");
      return;
    }
    if (location.pathname === "/servicios") {
      setActive("servicios");
      return;
    }
    const onScroll = () => {
      setScrolled(window.scrollY > 30);
      const offsets = LINKS.filter((l) => !l.route).map((l) => {
        const el = document.getElementById(l.id);
        if (!el) return { id: l.id, top: Infinity };
        return { id: l.id, top: Math.abs(el.getBoundingClientRect().top - 120) };
      });
      const closest = offsets.sort((a, b) => a.top - b.top)[0];
      if (closest) setActive(closest.id);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [location.pathname]);

  // Force scrolled bg when on a non-landing page so navbar stays readable
  const headerScrolled = scrolled || location.pathname !== "/";

  const handleNav = (link) => {
    setOpen(false);
    if (link.href) {
      window.open(link.href, "_blank", "noopener,noreferrer");
      return;
    }
    if (link.route) {
      navigate(link.route);
      window.scrollTo({ top: 0 });
      return;
    }
    if (location.pathname !== "/") {
      navigate(`/#${link.id}`);
      return;
    }
    const el = document.getElementById(link.id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const handleLogo = () => {
    setOpen(false);
    if (location.pathname !== "/") {
      navigate("/");
      return;
    }
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        headerScrolled
          ? "bg-[#001825]/85 backdrop-blur-xl border-b border-[#8f9194]/15 py-3"
          : "bg-transparent py-5"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 flex items-center justify-between">
        <button
          data-testid="nav-logo"
          onClick={handleLogo}
          className="flex items-center gap-3 group"
          aria-label="Spirits Production Services"
        >
          <img
            src={LOGO_URL}
            alt="SPS"
            className="h-10 md:h-12 w-auto object-contain mix-blend-screen transition-transform duration-500 group-hover:scale-105"
          />
        </button>

        <nav className="hidden lg:flex items-center gap-10 ml-auto">
          {LINKS.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-link-${l.id}`}
              onClick={() => handleNav(l)}
              className={`nav-link text-xs font-sans-modern font-medium uppercase tracking-[0.2em] ${
                active === l.id ? "active text-white" : ""
              }`}
            >
              {l.label}
            </button>
          ))}
        </nav>

        <button
          data-testid="mobile-menu-toggle"
          className="lg:hidden text-[#f8f9fa] p-2"
          onClick={() => setOpen((v) => !v)}
          aria-label="Menu"
        >
          {open ? <X size={22} strokeWidth={1.5} /> : <Menu size={22} strokeWidth={1.5} />}
        </button>
      </div>

      {open && (
        <div
          data-testid="mobile-menu"
          className="lg:hidden bg-[#001825]/95 backdrop-blur-xl border-t border-[#8f9194]/15 px-6 py-8"
        >
          <nav className="flex flex-col gap-5">
            {LINKS.map((l) => (
              <button
                key={l.id}
                data-testid={`mobile-nav-link-${l.id}`}
                onClick={() => handleNav(l)}
                className="text-left text-[#f8f9fa] text-base font-sans-modern uppercase tracking-[0.25em] border-b border-[#8f9194]/15 pb-3"
              >
                {l.label}
              </button>
            ))}
          </nav>
        </div>
      )}
    </header>
  );
};
